/**
 * Resposta de `POST /api/Swap/IncluirAutorizacaoManual` (corresponde ao C#
 * `SwapPreAutorizaResponseViewModel`, retornado direto via
 * `Ok(processamento.Retorno)` — SEM envelope `SwapApiResponse<T>`).
 *
 * Os campos refletem fielmente o contrato da API BIS-SWAP, inclusive
 * particularidades preservadas pelo backend que **não** seguem a
 * convenção do app:
 *  - `mesangemErro` → typo proposital ("mesangem" em vez de "mensagem");
 *  - `msgErroPOS`   → `POS` em maiúsculas (não `Pos`);
 *  - `flgAutorizado` é uma **string** `"S"`/`"N"` (NÃO boolean!).
 *
 * Interpretação do retorno (chega como HTTP 200 nos três casos):
 *  - `statusRetorno === 'OK' && flgAutorizado === 'S'` → autorizado.
 *  - `statusRetorno === 'NEGADO'` → recusa de negócio. Mostrar
 *    `mesangemErro`/`msgErroPOS`. **NÃO** é erro técnico — não cai
 *    no `catchError`.
 *  - `statusRetorno === 'ERRO'` → erro reportado pela BIS-SWAP.
 *    Idem ao NEGADO: mostrar mensagem; não é erro técnico.
 *
 * Falhas de infra (timeout, 4xx/5xx, 401 mesmo após retentativa)
 * vêm como HTTP error e devem ser tratadas com `catchError`.
 */
export type SwapPreAutorizaStatus = 'OK' | 'NEGADO' | 'ERRO';

/** Flag de autorização da BIS-SWAP. `'S'` = autorizado, `'N'` = não. */
export type SwapPreAutorizaFlag = 'S' | 'N';

export interface SwapPreAutorizaResponse {
  /** 'OK' | 'NEGADO' | 'ERRO'. Tipado como string para tolerar valores fora da enum. */
  statusRetorno: SwapPreAutorizaStatus | string;

  /** Código de erro retornado pela BIS-SWAP quando `statusRetorno !== 'OK'`. */
  codigoErro: string | null;

  /**
   * Mensagem de erro retornada pela BIS-SWAP. O nome preserva o typo
   * do contrato da API real (`mesangem` em vez de `mensagem`).
   */
  mesangemErro: string | null;

  /** Código de autorização emitido quando `flgAutorizado === 'S'`. */
  codAutorizacao: string | null;

  /** Valor efetivamente autorizado. */
  vlrAutorizado: number | null;

  /**
   * Flag de autorização — string `"S"` (autorizado) ou `"N"` (negado).
   * NÃO é boolean: o backend devolve string para preservar o contrato
   * original da BIS-SWAP. Use `isSwapAutorizacaoManualOk` para checar com
   * segurança.
   */
  flgAutorizado: SwapPreAutorizaFlag | string | null;

  /** ISO datetime de validade da autorização. */
  dataValidade: string | null;

  /** Mensagem de erro auxiliar exibível ao operador no POS. */
  msgErroPOS: string | null;
}

/**
 * True somente quando a autorização manual foi efetivada com sucesso pela
 * BIS-SWAP: `statusRetorno === 'OK'` e `flgAutorizado === 'S'`.
 *
 * Qualquer outra combinação (`NEGADO`, `ERRO`, `OK` com
 * `flgAutorizado='N'` ou ausente, objeto null/undefined) é considerada
 * falha de negócio.
 *
 * Implementação defensiva: o `flgAutorizado` é normalizado (trim +
 * upper-case) antes da comparação, para tolerar variações de espaçamento
 * ou caixa que possam vir da BIS-SWAP (ex.: `" S "`, `"s"`).
 */
export function isSwapAutorizacaoManualOk(
  r: SwapPreAutorizaResponse | null | undefined,
): boolean {
  if (!r || r.statusRetorno !== 'OK') {
    return false;
  }
  const flag = (r.flgAutorizado ?? '').toString().trim().toUpperCase();
  return flag === 'S';
}

/**
 * Extrai a mensagem de erro de um response da etapa de autorização manual
 * (BIS-SWAP, após `IncluirAutorizacaoManual` no ctf-services).
 *
 * Prioridade (do mais específico para o mais genérico):
 *  1. `mesangemErro` (mantém o typo do contrato);
 *  2. `msgErroPOS`;
 *  3. `codigoErro`.
 *
 * Retorna `null` quando nenhuma mensagem está preenchida — nesse caso
 * o front deve usar um fallback (ex.: "Autorização manual não foi
 * efetivada.").
 *
 * Use SOMENTE para HTTP 200 com `statusRetorno !== 'OK'`. Para falhas
 * de infra (4xx/5xx) continue tratando no `catchError` — são canais
 * diferentes.
 */
export function extrairMensagemSwapAutorizacaoManual(
  r: SwapPreAutorizaResponse | null | undefined,
): string | null {
  if (!r) {
    return null;
  }
  const m = (r.mesangemErro ?? '').trim();
  if (m) {
    return m;
  }
  const p = (r.msgErroPOS ?? '').trim();
  if (p) {
    return p;
  }
  const c = (r.codigoErro ?? '').trim();
  return c || null;
}
